import {
  Directive,
  ElementRef,
  AfterViewInit,
  NgZone,
  OnDestroy,
  ApplicationRef,
  EnvironmentInjector,
  Injector,
  Input,
  OnChanges,
  SimpleChanges,
  createComponent,
  ComponentRef,
  EmbeddedViewRef,
} from '@angular/core';
import {
  FieldAuditIndicatorComponent,
  FieldAuditEntry,
} from '../../components/field-audit-indicator/field-audit-indicator.component';
import { AuditRecord } from '../../core/models/audit.model';

@Directive({
  selector: '[appAutoAudit]',
  standalone: true,
})
export class AutoAuditDirective implements AfterViewInit, OnChanges, OnDestroy {
  /** Map of field label -> last audit record. Keys come from the backend's `field` label. */
  @Input() auditData: Record<string, AuditRecord> | null = null;
  /** When true (e.g. snapshot mode), skip indicator injection and clear any existing ones. */
  @Input() auditDisabled = false;

  private observer: MutationObserver | null = null;
  private componentRefs: ComponentRef<FieldAuditIndicatorComponent>[] = [];
  private processed = false;

  constructor(
    private el: ElementRef<HTMLElement>,
    private zone: NgZone,
    private appRef: ApplicationRef,
    private envInjector: EnvironmentInjector,
    private injector: Injector
  ) {}

  ngAfterViewInit() {
    this.zone.runOutsideAngular(() => {
      setTimeout(() => this.processContainers(), 200);

      this.observer = new MutationObserver(() => {
        if (!this.processed) {
          setTimeout(() => this.processContainers(), 100);
        }
      });
      this.observer.observe(this.el.nativeElement, { childList: true, subtree: true });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['auditDisabled']) {
      if (this.auditDisabled) {
        this.clearIndicators();
        this.processed = false;
      } else if (!changes['auditDisabled'].firstChange) {
        this.zone.runOutsideAngular(() => {
          setTimeout(() => this.processContainers(), 100);
        });
      }
    }
    if (changes['auditData'] && !changes['auditData'].firstChange) {
      this.clearIndicators();
      this.processed = false;
      this.zone.runOutsideAngular(() => {
        setTimeout(() => this.processContainers(), 100);
      });
    }
  }

  ngOnDestroy() {
    this.observer?.disconnect();
    this.clearIndicators();
  }

  private clearIndicators() {
    this.componentRefs.forEach(ref => {
      this.appRef.detachView(ref.hostView);
      ref.destroy();
    });
    this.componentRefs = [];

    // Unwrap .audit-field-row wrappers left in the DOM so processContainers
    // can re-create them cleanly on the next run.
    const wrappers = this.el.nativeElement.querySelectorAll('.audit-field-row');
    wrappers.forEach((wrapper: Element) => {
      const parent = wrapper.parentNode;
      if (parent) {
        while (wrapper.firstChild) {
          parent.insertBefore(wrapper.firstChild, wrapper);
        }
        parent.removeChild(wrapper);
      }
    });
  }

  private processContainers() {
    if (this.auditDisabled) return;
    if (!this.auditData || Object.keys(this.auditData).length === 0) return;

    const containers = this.el.nativeElement.querySelectorAll('.form-container, .field');
    containers.forEach((container: Element) => {
      if (
        container.querySelector('app-field-audit-indicator') ||
        container.querySelector('.audit-indicator')
      ) {
        return;
      }

      const label = container.querySelector('.label, label');
      if (!label) return;

      const fieldName = label.textContent?.trim() || '';

      const input = container.querySelector(
        'input, textarea, select, p-dropdown, p-calendar, p-checkbox, p-inputnumber, p-multiselect, p-fileupload'
      );
      if (!input) return;

      const controlName = this.extractFormControlName(input);
      const entry = this.findAuditEntryForField(fieldName, controlName);
      if (!entry) return;

      const audit: FieldAuditEntry = {
        fieldLabel: fieldName,
        date: entry.date,
        user: entry.user,
        before: entry.before,
        after: entry.after,
      };

      const compRef = createComponent(FieldAuditIndicatorComponent, {
        environmentInjector: this.envInjector,
        elementInjector: this.injector,
      });
      compRef.setInput('audit', audit);
      compRef.changeDetectorRef.detectChanges();
      this.appRef.attachView(compRef.hostView);
      this.componentRefs.push(compRef);

      const domElem = (compRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

      const isCheckboxContainer = !!container.querySelector('p-checkbox, input[type="checkbox"]');

      if (isCheckboxContainer) {
        (container as HTMLElement).style.position = 'relative';
        container.appendChild(domElem);
      } else {
        const existingRow = container.querySelector('.audit-field-row');
        if (existingRow) return;

        const controlEl = container.querySelector(
          'input.search-input, input.input, textarea, p-dropdown, p-calendar, p-inputnumber, p-multiselect'
        );
        if (controlEl) {
          const wrapper = document.createElement('div');
          wrapper.className = 'audit-field-row';
          wrapper.style.display = 'flex';
          wrapper.style.alignItems = 'center';
          wrapper.style.gap = '0';

          const parent = controlEl.parentNode;
          parent?.insertBefore(wrapper, controlEl);
          wrapper.appendChild(controlEl);
          (controlEl as HTMLElement).style.flex = '1';
          wrapper.appendChild(domElem);
        }
      }
    });

    this.processed = true;
  }

  private extractFormControlName(el: Element): string | null {
    return (
      el.getAttribute('formcontrolname') ??
      el.getAttribute('formControlName') ??
      el.getAttribute('ng-reflect-name') ??
      null
    );
  }

  private findAuditEntryForField(fieldLabel: string, controlName?: string): AuditRecord | null {
    if (!this.auditData) return null;
    const lower = fieldLabel.toLowerCase();

    for (const [key, record] of Object.entries(this.auditData)) {
      const recordField = record.field?.toLowerCase() ?? '';
      if (recordField === lower || key.endsWith(`:${fieldLabel}`)) {
        return record;
      }
      if (controlName) {
        const keyField = key.split(':').pop() ?? '';
        if (keyField === controlName) {
          return record;
        }
      }
    }

    return null;
  }
}

